import { FC } from 'react';
import styled from '@emotion/styled';
import CallForHelpWinkBox from 'components/call-for-help/CallForHelpWinkBox';
import { maxLenght, required, useForm } from 'components/core/form';
import { Text } from 'components/core/text';
import { Input } from 'components/form/Input';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import { formatPartialPhoneNumber } from 'helpers/transformers/formatPhoneNumber';

export interface InsuranceFormModel {
  sponsorId?: string;
  memberId?: string;
  groupNumber?: string;
  payerId?: string;
  phoneNumber?: string;
  memberFirstName?: string;
  memberLastName?: string;
  memberDob?: string;
  isMedicaid?: boolean;
  insuranceCardBack?: File;
  insuranceCardFront?: File;
  insuranceMemberId?: string;
  insuranceGroupId?: string;
  insurancePayerId?: string;
  insurancePhone?: string;
  insuranceCardBackUrl?: string;
  insuranceCardFrontUrl?: string;
  leadId?: string;
}

interface InsuranceFormProps {
  childName: string;
  initialValues?: Partial<InsuranceFormModel>;
  handleSubmit: (data: InsuranceFormModel) => void;
  hidePayerId?: boolean;
  hideGroupNumber?: boolean;
  hideInsurancePhoneNumber?: boolean;
  hideMemberId?: boolean;
  onChange?: () => void;
}

const Wrapper = styled.div`
  > * {
    padding-bottom: 24px;
  }
`;

export const InsuranceForm: FC<InsuranceFormProps> = ({
  childName,
  initialValues,
  handleSubmit,
  hidePayerId,
  hideGroupNumber,
  hideInsurancePhoneNumber,
  hideMemberId,
  onChange,
}) => {
  const { isSelf } = useCurrentUser();
  const { registerField, registerSubmitCallback } = useForm<InsuranceFormModel>();

  const formattedPhoneNumber = initialValues?.phoneNumber
    ? formatPartialPhoneNumber(initialValues.phoneNumber)
    : undefined;

  registerSubmitCallback((isValid, data) => {
    if (isValid) handleSubmit(data);
  });

  return (
    <Wrapper>
      {!hideMemberId && (
        <Input
          requiredStar
          label="Member ID"
          data-testid="insurance-form-member-id"
          defaultValue={initialValues?.insuranceMemberId ?? initialValues?.memberId}
          onChange={onChange}
          {...registerField('memberId', {
            validators: [required],
          })}
        />
      )}
      {!hideGroupNumber && (
        <Input
          requiredStar
          label="Group Number"
          data-testid="insurance-form-group-number"
          defaultValue={initialValues?.insuranceGroupId ?? initialValues?.groupNumber}
          onChange={onChange}
          {...registerField('groupNumber', {
            validators: [required],
          })}
        />
      )}
      {!hidePayerId && (
        <Input
          requiredStar
          label="Payer ID"
          maxLength={5}
          minLength={5}
          data-testid="insurance-form-payer-id"
          defaultValue={initialValues?.insurancePayerId ?? initialValues?.payerId}
          onChange={onChange}
          {...registerField('payerId', {
            validators: [required, maxLenght(5, true, 'Payer ID is 5 characters.')],
          })}
        />
      )}
      {!hideInsurancePhoneNumber && (
        <Input
          requiredStar
          label="Insurance phone number for providers"
          data-testid="insurance-form-phone-number"
          placeholder="(XXX) XXX-XXXX"
          defaultValue={initialValues?.insurancePhone ?? formattedPhoneNumber}
          onChange={onChange}
          {...registerField('phoneNumber', {
            mask: formatPartialPhoneNumber,
            validators: [required],
          })}
        />
      )}

      <CallForHelpWinkBox margin="0 0 40px 0" />

      <div>
        <Text variant="text4">
          {`Verify ${isSelf ? 'your' : `${childName}'s`} information (as registered with insurance):`}
        </Text>
      </div>

      <Input
        requiredStar
        label="First Name"
        data-testid="memberFirstName"
        defaultValue={initialValues?.memberFirstName}
        onChange={onChange}
        {...registerField('memberFirstName', {
          validators: [required],
        })}
      />
      <Input
        requiredStar
        label="Last Name"
        data-testid="memberLastName"
        defaultValue={initialValues?.memberLastName}
        onChange={onChange}
        {...registerField('memberLastName', {
          validators: [required],
        })}
      />
      <Input
        requiredStar
        label="Date of Birth"
        data-testid="memberDob"
        defaultValue={initialValues?.memberDob}
        onChange={onChange}
        {...registerField('memberDob', {
          validators: [required],
        })}
        type="date"
      />
    </Wrapper>
  );
};
